import { Boundaries } from "@planzone/core";
import { allOrganizationalUnitsFilter } from "@planzone/dto";
import { events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = {
  organizationalUnits: [],
  filter: allOrganizationalUnitsFilter,
  totalPages: 0,
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
};

function handleRequest(
  state: State,
  { filter }: Boundaries["retrieveAllOrganizationalUnits"]["request"],
): State {
  return filter.page === 0
    ? {
        organizationalUnits: [],
        request: setRequestStateToPending(),
        filter: { ...filter, page: filter.page + 1 },
        totalPages: 0,
      }
    : {
        ...state,
        request: setRequestStateToPending(),
        filter: { ...filter, page: filter.page + 1 },
      };
}

function handleSuccess(
  state: State,
  {
    organizationalUnits,
    filter,
    totalPages,
  }: Boundaries["retrieveAllOrganizationalUnits"]["success"],
): State {
  return {
    ...state,
    organizationalUnits:
      filter.page === 0
        ? organizationalUnits
        : [...state.organizationalUnits, ...organizationalUnits],
    request: setRequestStateToSuccess(),
    totalPages,
  };
}

function handleFailure(state: State): State {
  return { ...state, request: setRequestStateToFailure() };
}

// The list follows the units created, renamed and removed on the OBS page, within the current search.
function matchesSearch(state: State, name: string) {
  return name.toLowerCase().includes(state.filter.name.toLowerCase());
}

function handleCreateOrganizationalUnitSuccess(
  state: State,
  { organizationalUnit }: Boundaries["createOrganizationalUnit"]["success"],
): State {
  const { resources, ...createdOrganizationalUnit } = organizationalUnit;

  return matchesSearch(state, organizationalUnit.name)
    ? {
        ...state,
        organizationalUnits: [
          ...state.organizationalUnits,
          createdOrganizationalUnit,
        ],
      }
    : state;
}

function handleEditOrganizationalUnitSuccess(
  state: State,
  {
    organizationalUnit,
  }:
    | Boundaries["editOrganizationalUnit"]["success"]
    | Boundaries["updateOrganizationalUnit"]["success"],
): State {
  return {
    ...state,
    organizationalUnits: state.organizationalUnits.map((unit) =>
      unit.uuid === organizationalUnit.uuid
        ? { ...unit, name: organizationalUnit.name }
        : unit,
    ),
  };
}

function handleRemoveOrganizationalUnitSuccess(
  state: State,
  {
    organizationalUnitUuids,
  }: Boundaries["removeOrganizationalUnit"]["success"],
): State {
  return {
    ...state,
    organizationalUnits: state.organizationalUnits.filter(
      (unit) => !organizationalUnitUuids.includes(unit.uuid),
    ),
  };
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveAllOrganizationalUnitsRequested]: handleRequest,
    [events.retrieveAllOrganizationalUnitsSucceeded]: handleSuccess,
    [events.retrieveAllOrganizationalUnitsFailed]: handleFailure,
    [events.createOrganizationalUnitSucceeded]:
      handleCreateOrganizationalUnitSuccess,
    [events.editOrganizationalUnitSucceeded]:
      handleEditOrganizationalUnitSuccess,
    [events.updateOrganizationalUnitSucceeded]:
      handleEditOrganizationalUnitSuccess,
    [events.removeOrganizationalUnitSucceeded]:
      handleRemoveOrganizationalUnitSuccess,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
